import { HTTPServiceClient } from '@/packages/http/service';
import * as t from './endpoints';
import Investment from '@/models/Investment';
import Submission from '@/models/Submission';
import {
  DownloadErrorReportsForBatchResponse,
  FetchErrorReportsForBatchUrlResponse,
  TemplateUploadParams
} from './types';
import { TEMPLATE_SOURCE } from '@/utils/constants';
import Auth from '@/packages/Auth';
import { OrganisationAssociationMeta } from '@/models/OrganisationAssociation';

/**
 * --------------------------------------------------
 *	Submission HTTP Serice
 * --------------------------------------------------
 **/
export const SubmissionService = {
  async fetchInvestment(investment_id: string): Promise<Investment> {
    const { data } = await HTTPServiceClient.get(
      t.INVESTMENT_ENDPOINT(investment_id),
      {
        params: {
          include:
            'entity,timePeriod,rootInvestment.entity,entity.strategy,entity.productType,' +
            'latestSubmission,latestSubmission.notes,submissionRequest,entity.subStrategy,latestSubmission.submitter'
        }
      }
    );
    return data.data;
  },

  async fetchManagerAssociationMeta(
    investment_id: string
  ): Promise<OrganisationAssociationMeta> {
    try {
      const { data } = await HTTPServiceClient.get(
        t.MANAGER_ASSOCIATION_META_ENDPOINT(investment_id)
      );
      return data?.data ?? {};
    } catch (e) {
      throw new Error(e?.data?.message ?? 'Something went wrong');
    }
  },

  async submitTemplate(paramData: TemplateUploadParams): Promise<Submission> {
    const url = t.SUBMISSION_ENDPOINT();
    let formData = new FormData();

    if (paramData.preUploadedFileKey) {
      formData.append('preUploadedFileKey', paramData.preUploadedFileKey);
    } else {
      formData.append('file', paramData.file, paramData.file.name);
    }
    formData.append('fileOriginalName', paramData.file.name);

    formData.append('investmentId', paramData.investmentId);
    formData.append('source', paramData.source);
    if (paramData.startDate && paramData.endDate) {
      formData.append('startDate', paramData.startDate);
      formData.append('endDate', paramData.endDate);
    }
    if (paramData.batchId) {
      formData.append('batchId', paramData.batchId);
    }

    const templateSource = Auth.userIsImpersonated()
      ? TEMPLATE_SOURCE.platformWithImpersonation
      : TEMPLATE_SOURCE.platform;
    formData.append('templateSource', templateSource);
    const { data } = await HTTPServiceClient.post(url, formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
    return data.data;
  },

  async fetchSubmission(submission_id: string): Promise<Submission> {
    const { data } = await HTTPServiceClient.get(
      t.SUBMISSION_ENDPOINT(submission_id)
    );
    return data.data;
  },

  async changeSubmissionStatus(paramData: any): Promise<Submission> {
    const { data } = await HTTPServiceClient.patch(
      t.SUBMISSION_ENDPOINT(paramData.submissionId),
      {
        status: paramData.status
      }
    );
    return data.data;
  },

  async requestSubmissionReopen(paramData: any): Promise<any> {
    const { data } = await HTTPServiceClient.post(
      t.SUBMISSION_REQUEST_REOPEN_ENDPOINT(),
      paramData
    );
    return data.data;
  },

  async fetchSubmissionErrors(submission_id: string): Promise<any> {
    const { data } = await HTTPServiceClient.get(
      t.SUBMISSION_ERRORS_ENDPOINT(submission_id)
    );
    return data;
  },

  async downloadErrorReport(submission_id: string): Promise<any> {
    const { data } = await HTTPServiceClient.get(
      t.ERROR_REPORT_DOWNLOAD_ENDPOINT(submission_id)
    );
    return data;
  },

  async storeBypassedErrors(paramData: any): Promise<any> {
    const {
      data
    } = await HTTPServiceClient.post(
      t.SUBMISSION_BYPASSABLE_ERRORS_ENDPOINT(paramData.id),
      { ...paramData.postData }
    );
    return data;
  },

  async fetchSubmissionsForBatch(batchId: string): Promise<Submission[]> {
    const { data } = await HTTPServiceClient.get(
      t.FETCH_SUBMISSION_FOR_BATCH(batchId)
    );
    return data.data;
  },

  async downloadErrorReportsForBatch(
    batchId: string
  ): Promise<DownloadErrorReportsForBatchResponse> {
    const { data } = await HTTPServiceClient.post(
      t.DOWNLOAD_ERROR_REPORTS_FOR_BATCH(batchId)
    );
    return data;
  },

  async fetchErrorReporsUrlForBatch(
    jobId: string
  ): Promise<FetchErrorReportsForBatchUrlResponse> {
    const { data } = await HTTPServiceClient.get(
      t.FETCH_ERROR_REPORTS_URL_FOR_BATCH(jobId)
    );
    return data;
  }
};
